/**
 * Workspace service - handles all workspace-related API operations
 */

import { BaseApiClient } from '../core/base-client';
import { 
  Workspace, 
  WorkspaceResponse, 
  WorkspaceFullData, 
  WorkspaceSearchFilters,
  AvailabilityResponse,
  AvailabilityCheckRequest,
  BookingType 
} from '../types';

export class WorkspaceService {
  constructor(private client: BaseApiClient) {}

  /**
   * Get workspaces with optional search query and filters
   */
  async getWorkspaces(searchQuery?: string, filters?: WorkspaceSearchFilters): Promise<WorkspaceResponse>;
  async getWorkspaces(filters?: WorkspaceSearchFilters): Promise<WorkspaceResponse>;
  async getWorkspaces(searchQueryOrFilters?: string | WorkspaceSearchFilters, filters?: WorkspaceSearchFilters): Promise<WorkspaceResponse> {
    // Handle overloaded parameters
    let searchQuery: string | undefined;
    let actualFilters: WorkspaceSearchFilters | undefined;
    
    if (typeof searchQueryOrFilters === 'string') {
      searchQuery = searchQueryOrFilters;
      actualFilters = filters;
    } else {
      searchQuery = undefined;
      actualFilters = searchQueryOrFilters;
    }
    
    // Convert filters to query parameters for GET request
    const queryParams = new URLSearchParams();
    
    // Add search query if provided
    if (searchQuery) {
      queryParams.append('searchQuery', searchQuery);
    }
    
    if (actualFilters) {
      Object.entries(actualFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            // Handle array values (like amenities)
            value.forEach(v => queryParams.append(key, v.toString()));
          } else if (value instanceof Date) {
            // Handle Date objects
            queryParams.append(key, value.toISOString());
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });
    }

    const queryString = queryParams.toString();
    const url = queryString ? `/api/workspaces?${queryString}` : '/api/workspaces';
    
    return this.client.get<WorkspaceResponse>(url);
  }

  /**
   * Get a single workspace by ID
   */
  async getWorkspace(id: string): Promise<Workspace> {
    return this.client.get<Workspace>(`/api/workspaces/${id}`);
  }

  /**
   * Get full workspace data with detailed information
   */
  async getWorkspaceFullData(id: string): Promise<WorkspaceFullData> {
    return this.client.get<WorkspaceFullData>(`/api/workspaces/${id}/full`);
  }

  /**
   * Check workspace availability for given time period
   */
  async checkAvailability(request: AvailabilityCheckRequest): Promise<AvailabilityResponse> {
    const { id, startDateTime, endDateTime, bookingType = BookingType.DAILY } = request;
    
    // Convert parameters to query string for GET request
    const queryParams = new URLSearchParams({
      startDateTime: startDateTime.toLocaleString('sv').replace(' ', 'T'),
      endDateTime: endDateTime.toLocaleString('sv').replace(' ', 'T'),
      bookingType: bookingType
    });

    const response = await this.client.get<{
      success: boolean;
      data: AvailabilityResponse;
      message: string;
    }>(`/api/workspaces/${id}/availability?${queryParams.toString()}`);

    return response.data;
  }

  /**
   * Get workspaces by property ID
   */
  async getSpacesByProperty(propertyId: string): Promise<WorkspaceResponse> {
    return this.client.get<WorkspaceResponse>(`/api/workspaces/property/${propertyId}`);
  }
}

/**
 * Factory function to create workspace service
 */
export function createWorkspaceService(client: BaseApiClient): WorkspaceService {
  return new WorkspaceService(client);
} 